// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Control } from '../Control';
import HotkeysEditControlTemplate from '@front/templates/HotkeysEditControlTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { HotkeysKeyless } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { popupErrorHandler } from '@shared/utils/AppHandlers';
import { Rectangle } from '@shared/commons/Geometry';
import { MessageBoxType } from '@shared/utils/MessageBoxUtils';
import { TerceraMessageBox } from '../../screen/TerceraMessageBox';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
export class HotkeysEditControl extends Control {
    constructor() {
        super();
        this.oldValue = '';
        this.hotkeysItem = null;
        this.shiftedKeysToNumbers = new Map([
            ['!', '1'],
            ['@', '2'],
            ['#', '3'],
            ['$', '4'],
            ['%', '5'],
            ['^', '6'],
            ['&', '7'],
            ['*', '8'],
            ['(', '9'],
            [')', '0']
        ]);
        this.startReadHotkeys = () => {
            const input = this.find('input');
            if (input instanceof HTMLInputElement) {
                input.select();
            }
            this.oldValue = this.get('value');
            void this.set('focused', true);
            document.addEventListener('keydown', this.onGlobalKeyDown);
        };
        this.stopReadHotkeys = () => {
            this.saveNewValue();
            void this.set('focused', false);
            this.hidePopupError();
            document.removeEventListener('keydown', this.onGlobalKeyDown);
        };
        this.onGlobalKeyDown = (event) => {
            const { key } = event;
            this.stopBaseEvent(event);
            if (key === 'Escape' || key === 'Esc') {
                void this.set('hasError', true);
                this.find('input').blur();
            }
            if (key === 'Enter') {
                this.find('input').blur();
            }
            if (key === 'Backspace' || key === 'Delete') {
                this.onValueChanged(HotkeysKeyless);
                this.hidePopupError();
                return;
            }
            const combinationString = this.getCombinationString(event);
            if (isValidString(combinationString)) {
                this.onValueChanged(combinationString);
            }
        };
    }
    getType() {
        return 'HotkeysEditControl';
    }
    setData(item) {
        if (isNullOrUndefined(item)) {
            return;
        }
        void this.set({
            value: item.Hotkey,
            text: this.localizeProperty(item.ActionName)
        });
        this.hotkeysItem = item;
    }
    localizeProperty(key) {
        const locKey = 'property.Hotkeys.' + key;
        return Resources.getResource(locKey);
    }
    oncomplete() {
        super.oncomplete();
        this.on('focus', this.startReadHotkeys);
        this.on('blur', this.stopReadHotkeys);
        this.on('keyDown', function () { });
        this.observe('item', this.setData);
        this.localize();
    }
    onteardown() {
        this.hotkeysItem.TemporaryHotkey = '';
    }
    onSubmitEvent(event) {
        if (event === null || event === void 0 ? void 0 : event.original) {
            event.original.preventDefault();
        }
    }
    stopBaseEvent(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    getCombinationString(event) {
        const { key, ctrlKey, altKey, shiftKey, metaKey } = event;
        const combination = [];
        if (ctrlKey) {
            combination.push('CTRL');
        }
        if (metaKey) {
            combination.push('COMMAND');
        }
        if (shiftKey) {
            combination.push('SHIFT');
        }
        if (altKey) {
            combination.push('ALT');
        }
        if (!['Control', 'Command', 'Shift', 'Alt'].includes(key)) {
            const keyPressed = key.match(/^[a-zA-Z0-9!@#$%^&*()]+$/);
            if (keyPressed) {
                if (key.match(/^[a-zA-Z]$/)) {
                    combination.push(key.toUpperCase());
                }
                else if (key.match(/^[0-9]$/) || this.shiftedKeysToNumbers.get(key)) {
                    const actualKey = this.shiftedKeysToNumbers.get(key) || key;
                    combination.push(actualKey);
                }
            }
        }
        if (combination.length) {
            if (!['CTRL', 'COMMAND', 'SHIFT', 'ALT'].includes(combination[0])) {
                this.showPopupError(Resources.getResource('hotkeysPopupMessage.ErrorText.MustCombination'));
            }
            else {
                if (['CTRL', 'COMMAND', 'SHIFT', 'ALT'].includes(combination[combination.length - 1])) {
                    this.showPopupError(Resources.getResource('hotkeysPopupMessage.ErrorText.NeedAddLetterOrNumber'));
                }
                else {
                    this.hidePopupError();
                }
            }
        }
        return combination.join('+');
    }
    localize() {
        void this.set('tooltip', Resources.getResource('property.ClickToModify'));
    }
    onValueChanged(value) {
        void this.set('value', value);
    }
    saveNewValue() {
        if (this.get('hasError') || this.checkForDuplicate()) {
            void this.set('value', this.oldValue);
            this.oldValue = '';
        }
        else {
            const newValue = this.get('value');
            this.oldValue = '';
            this.hotkeysItem.TemporaryHotkey = newValue;
            this.fire(HotkeysEditControlEvents.ValueChanged, this.hotkeysItem, newValue);
        }
    }
    showPopupError(errorText) {
        if (!this.get('enabled') || !this.get('focused') || !isValidString(errorText)) {
            return;
        }
        void this.set('hasError', true);
        popupErrorHandler.Show(this, errorText, '', null, null, 'right');
    }
    hidePopupError() {
        void this.set('hasError', false);
        popupErrorHandler.Hide(this);
    }
    checkForDuplicate() {
        const arrayHotkeys = HotkeysManager.GetFilteredArrayHotkeys();
        const currentValue = this.get('value');
        if (arrayHotkeys.includes(currentValue) && this.oldValue !== currentValue) {
            this.showMessageBox();
            return true;
        }
        return false;
    }
    getErrorTextMessageBox() {
        const hotkeysMap = HotkeysManager.GetHotkeysMap();
        for (const key of hotkeysMap.keys()) {
            for (const item of hotkeysMap.get(key)) {
                const { Hotkey, ActionName, TemporaryHotkey } = item;
                const hotkey = isValidString(TemporaryHotkey) ? TemporaryHotkey : Hotkey;
                if (hotkey === this.get('value')) {
                    const action = Resources.getResource('property.Hotkeys.' + ActionName);
                    return Resources.getResource('hotkeysMessageBox.ErrorText')
                        .replace('{0}', action)
                        .replace('{1}', hotkey);
                }
            }
        }
    }
    showMessageBox() {
        const addData = { okText: Resources.getResource('general.messageBox.ok') };
        TerceraMessageBox.Show(Resources.getResource('screen.error.title'), this.getErrorTextMessageBox(), MessageBoxType.Info, null, null, false, true, null, addData);
    }
    getAbsoluteRectangle() {
        const rect = this.find('input').getBoundingClientRect();
        return new Rectangle(rect.left, rect.top, rect.width, rect.height);
    }
}
export var HotkeysEditControlEvents;
(function (HotkeysEditControlEvents) {
    HotkeysEditControlEvents["ValueChanged"] = "ValueChanged";
})(HotkeysEditControlEvents || (HotkeysEditControlEvents = {}));
Control.extendWith(HotkeysEditControl, {
    template: HotkeysEditControlTemplate,
    data: function () {
        return {
            value: '',
            tooltip: '',
            hasError: false
        };
    }
});
